import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Typography, Grid, IconButton, Tooltip, Chip, CircularProgress, Snackbar, Alert, Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import { useParams } from "react-router-dom";
import { getImages, imageUpload, imageDelete, reorderImages } from "../../api/imageUploadApi";

// ── Drag-to-reorder helper ──────────────────────────────────
const reorder = (list, from, to) => {
  const result = [...list];
  const [removed] = result.splice(from, 1);
  result.splice(to, 0, removed);
  return result;
};

// ── Draggable image card ────────────────────────────────────
const DraggableCard = ({ url, index, label, badge, onDelete, onDragStart, onDragOver, onDrop, isDraggingOver }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDrop={() => onDrop(index)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative", borderRadius: 2, overflow: "hidden", cursor: "grab",
        border: isDraggingOver ? "2px dashed #667eea" : "2px solid transparent",
        transition: "border 0.15s, transform 0.15s, boxShadow 0.15s",
        transform: isDraggingOver ? "scale(1.03)" : "scale(1)",
        boxShadow: hovered ? "0 8px 24px rgba(0,0,0,0.18)" : "0 2px 8px rgba(0,0,0,0.1)",
        bgcolor: "#f8fafc",
        userSelect: "none",
      }}
    >
      {/* Drag handle */}
      <Box sx={{
        position: "absolute", top: 6, left: 6, zIndex: 3,
        bgcolor: "rgba(0,0,0,0.45)", borderRadius: 1, p: 0.3,
        display: "flex", alignItems: "center", opacity: hovered ? 1 : 0,
        transition: "opacity 0.2s",
      }}>
        <DragIndicatorIcon sx={{ fontSize: 16, color: "#fff" }} />
      </Box>

      {/* Position badge */}
      <Box sx={{
        position: "absolute", top: 6, left: 32, zIndex: 3,
        bgcolor: index === 0 ? "#667eea" : "rgba(0,0,0,0.5)",
        color: "#fff", borderRadius: 1, px: 0.8, py: 0.2,
        fontSize: 11, fontWeight: 700,
      }}>
        {index === 0 ? "Hero" : `#${index + 1}`}
      </Box>

      {/* Image */}
      <Box
        component="img"
        src={url}
        alt={label}
        sx={{ width: "100%", height: 150, objectFit: "cover", display: "block", pointerEvents: "none" }}
      />

      {/* Overlay with filename */}
      <Box sx={{
        px: 1, py: 0.5, bgcolor: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Typography variant="caption" noWrap sx={{ flex: 1, color: "#64748b", fontSize: 11 }}>
          {label}
        </Typography>
        {onDelete && (
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(url)} sx={{ ml: 0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// ── Main component ──────────────────────────────────────────
const ImageManagerPage = () => {
  const { schema, id } = useParams();

  const [existingImages, setExistingImages] = useState([]);
  const [existingName, setExistingName] = useState("");
  const [newFiles, setNewFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false); // drop-zone drag
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [newDragFrom, setNewDragFrom] = useState(null);
  const [newDragOver, setNewDragOver] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });
  const [orderChanged, setOrderChanged] = useState(false);

  const showSnack = (msg, severity = "success") => setSnackbar({ open: true, msg, severity });

  useEffect(() => {
    if (!id || !schema) return;
    getImages(schema, id)
      .then((res) => {
        setExistingImages(res.images || []);
        setExistingName(res.name || "Document");
      })
      .catch(() => showSnack("Failed to load images", "error"));
  }, [schema, id]);

  // ── Drop zone for new files ─────────────────────────────
  const handleDropZoneDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
    if (!files.length) return;
    setNewFiles(prev => [...prev, ...files]);
    setPreviewUrls(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewFiles(prev => [...prev, ...files]);
    setPreviewUrls(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const handleRemovePreview = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // ── Upload ──────────────────────────────────────────────
  const handleUpload = async () => {
    if (!newFiles.length) { showSnack("Please select images first", "warning"); return; }
    setUploading(true);
    const formData = new FormData();
    newFiles.forEach(f => formData.append("images", f));
    try {
      const res = await imageUpload(schema, id, formData);
      setExistingImages(res.data.images || []);
      setNewFiles([]);
      setPreviewUrls([]);
      setOrderChanged(false);
      showSnack("Upload successful!");
    } catch {
      showSnack("Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────
  const handleDelete = async (imageUrl) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      const res = await imageDelete(schema, id, { data: { imageUrl } });
      setExistingImages(res.data.images || []);
      showSnack("Image deleted");
    } catch {
      showSnack("Delete failed", "error");
    }
  };

  // ── Drag-to-reorder existing ────────────────────────────
  const handleExistingDragStart = (i) => setDragFromIndex(i);
  const handleExistingDragOver = (i) => setDragOverIndex(i);
  const handleExistingDrop = (i) => {
    if (dragFromIndex === null || dragFromIndex === i) { setDragFromIndex(null); setDragOverIndex(null); return; }
    setExistingImages(prev => reorder(prev, dragFromIndex, i));
    setDragFromIndex(null);
    setDragOverIndex(null);
    setOrderChanged(true);
  };

  // ── Drag-to-reorder new previews ────────────────────────
  const handleNewDragStart = (i) => setNewDragFrom(i);
  const handleNewDragOver = (i) => setNewDragOver(i);
  const handleNewDrop = (i) => {
    if (newDragFrom === null || newDragFrom === i) { setNewDragFrom(null); setNewDragOver(null); return; }
    setNewFiles(prev => reorder(prev, newDragFrom, i));
    setPreviewUrls(prev => reorder(prev, newDragFrom, i));
    setNewDragFrom(null);
    setNewDragOver(null);
  };

  // ── Save reorder ────────────────────────────────────────
  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      if (reorderImages) {
        await reorderImages(schema, id, { images: existingImages });
      }
      setOrderChanged(false);
      showSnack("Image order saved!");
    } catch {
      showSnack("Failed to save order", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
            Image Manager
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            {existingName.toUpperCase()} &nbsp;·&nbsp; {existingImages.length} uploaded · Drag cards to reorder · First image = Hero
          </Typography>
        </Box>
        {orderChanged && (
          <Button
            variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveOrder} disabled={saving}
            sx={{ background: "linear-gradient(135deg, #667eea, #764ba2)", fontWeight: 700 }}
          >
            Save Order
          </Button>
        )}
      </Box>

      {/* Note */}
      <Paper sx={{ p: 1.5, mb: 3, bgcolor: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: "#92400e" }}>
          📐 <strong>Hero image</strong> (position #1): 1920×1080 &nbsp;|&nbsp; <strong>Gallery images</strong>: 1080×720
        </Typography>
      </Paper>

      {/* ── Drop zone ─────────────────────────────────────── */}
      <Box
        component="label"
        htmlFor="multi-image-input"
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => { handleDropZoneDrop(e); }}
        sx={{
          border: `2px dashed ${isDragOver ? "#667eea" : "#cbd5e1"}`,
          borderRadius: 3, p: 5, mb: 2,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5,
          bgcolor: isDragOver ? "#eef2ff" : "#f8fafc",
          cursor: "pointer", transition: "all 0.2s",
          "&:hover": { borderColor: "#667eea", bgcolor: "#eef2ff" },
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: isDragOver ? "#667eea" : "#94a3b8" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDragOver ? "#667eea" : "#475569" }}>
          {isDragOver ? "Drop images here" : "Drag & drop images here"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#94a3b8" }}>or click anywhere here to browse</Typography>
        <Chip label="Hold Ctrl / Cmd to select multiple files" size="small" sx={{ bgcolor: "#e0e7ff", color: "#4338ca" }} />
        <input
          id="multi-image-input"
          type="file"
          multiple
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
          onClick={(e) => { e.target.value = null; }}
        />
      </Box>
      {/* Explicit browse button as fallback */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Button component="label" htmlFor="multi-image-input" variant="outlined" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2 }}>
          Browse &amp; Select Images
        </Button>
      </Box>

      {/* ── New files preview ──────────────────────────────── */}
      {previewUrls.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Selected Images &nbsp;
              <Chip label={`${previewUrls.length} file${previewUrls.length > 1 ? "s" : ""}`} size="small" color="primary" />
            </Typography>
            <Button
              variant="contained" startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
              onClick={handleUpload} disabled={uploading}
              sx={{ background: "linear-gradient(135deg, #10b981, #059669)", fontWeight: 700 }}
            >
              {uploading ? "Uploading…" : "Upload All"}
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mb: 1.5 }}>
            Drag to reorder before uploading — first card will be the Hero image
          </Typography>
          <Grid container spacing={2}>
            {previewUrls.map((url, idx) => (
              <Grid item key={idx} xs={6} sm={4} md={3}>
                <DraggableCard
                  url={url}
                  index={idx}
                  label={newFiles[idx]?.name || ""}
                  onDelete={() => handleRemovePreview(idx)}
                  onDragStart={handleNewDragStart}
                  onDragOver={handleNewDragOver}
                  onDrop={handleNewDrop}
                  isDraggingOver={newDragOver === idx && newDragFrom !== idx}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ── Existing images ────────────────────────────────── */}
      {existingImages.length > 0 && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Uploaded Images
            </Typography>
            <Chip label={`${existingImages.length} images`} size="small" sx={{ bgcolor: "#e0e7ff", color: "#4338ca" }} />
            {orderChanged && (
              <Chip icon={<CheckCircleIcon />} label="Order changed — save to apply" size="small" color="warning" variant="outlined" />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mb: 1.5 }}>
            Drag cards to reorder · Click Save Order to persist
          </Typography>
          <Grid container spacing={2}>
            {existingImages.map((url, idx) => (
              <Grid item key={url} xs={6} sm={4} md={3}>
                <DraggableCard
                  url={url}
                  index={idx}
                  label={url.split("/").pop()}
                  onDelete={handleDelete}
                  onDragStart={handleExistingDragStart}
                  onDragOver={handleExistingDragOver}
                  onDrop={handleExistingDrop}
                  isDraggingOver={dragOverIndex === idx && dragFromIndex !== idx}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {existingImages.length === 0 && previewUrls.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
          <CloudUploadIcon sx={{ fontSize: 64, mb: 1 }} />
          <Typography>No images yet. Drop files above to get started.</Typography>
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImageManagerPage;
